<template>
  <Layout>
    <Head title="Pending Requests - Kiosk" />

    <div class="p-4 sm:p-6 md:p-8">
      <div class="space-y-6">
        <div class="border-b pb-4">
          <h1 class="text-2xl font-bold text-gray-900">{{ t('invites.title') }}</h1>
          <p class="text-gray-600 mt-1">{{ t('invites.review_manage') }}</p>
        </div>

        <Card>
          <CardHeader>
            <CardTitle>{{ t('invites.your_requests') }}</CardTitle>
            <CardDescription>
              <template v-if="invites.length > 0">
                {{ t('invites.request_count', { count: invites.length }) }}
              </template>
              <template v-else>
                {{ t('invites.no_pending') }}
              </template>
            </CardDescription>
          </CardHeader>

          <CardContent>
            <template v-if="invites.length > 0">
              <div class="space-y-4">
                <div
                  v-for="invite in invites"
                  :key="invite.id"
                  class="border rounded-lg p-4 hover:shadow-md transition-shadow"
                >
                  <div class="flex items-start justify-between">
                    <div class="space-y-3">
                      <div>
                        <h3 class="font-medium text-gray-900">
                          {{ invite.appointment_type_name || invite.legal_case_type_name }}
                        </h3>
                        <p class="text-sm text-gray-600">
                          {{ invite.legal_case_type_description }}
                        </p>
                      </div>

                      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3 text-sm">
                        <div v-if="invite.location" class="flex items-start gap-2 text-gray-600">
                          <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                          </svg>
                          <div>
                            <span class="font-medium">{{ t('common.location') }}</span>
                            <span class="ml-1">{{ invite.location }}</span>
                          </div>
                        </div>

                        <div v-if="invite.primary_attorney_name" class="flex items-start gap-2 text-gray-600">
                          <svg class="h-4 w-4 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                          </svg>
                          <div>
                            <span class="font-medium">{{ t('common.attorney') }}</span>
                            <span class="ml-1">{{ invite.primary_attorney_name }}</span>
                            <span v-if="invite.primary_attorney_email" class="block text-xs text-gray-500">
                              {{ invite.primary_attorney_email }}
                            </span>
                          </div>
                        </div>
                      </div>

                      <div class="flex items-center gap-4 text-xs text-gray-500">
                        <span>{{ t('invites.received') }} {{ formatDate(invite.created_at) }}</span>
                      </div>
                    </div>
                    <Link
                      :href="`/invites/${invite.id}`"
                      class="inline-flex items-center px-3 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors"
                    >
                      {{ t('invites.view_details') }}
                    </Link>
                  </div>
                </div>
              </div>
            </template>

            <template v-else>
              <div class="text-center py-12">
                <div class="mx-auto h-24 w-24 text-gray-400">
                  <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="1" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                </div>
                <h3 class="mt-4 text-sm font-medium text-gray-900">{{ t('invites.title') }}</h3>
                <p class="mt-2 text-sm text-gray-500">
                  {{ t('invites.no_pending_message') }}
                </p>
              </div>
            </template>
          </CardContent>
        </Card>
      </div>
    </div>
  </Layout>
</template>

<script setup>
import { Head, Link } from '@inertiajs/vue3'

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import Layout from '@/Layouts/Minimal.vue'
import { t } from '@/entrypoints/translations.js'

// Props
defineProps({
  invites: Object,
})

// Helper function to format dates
const formatDate = (dateString) => {
  const date = new Date(dateString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  })
}
</script>
